import React from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, GripVertical } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from './ui/tooltip';

const stateStyles = {
  healthy: {
    border: 'border-l-[#1D9E75] border-[#1D9E75]/30',
    bg: 'bg-[#1D9E75]/5',
    icon: CheckCircle2,
    iconColor: 'text-[#1D9E75]',
    badgeColor: 'bg-[#1D9E75]'
  },
  broken: {
    border: 'border-l-[#E63946] border-[#E63946]/30',
    bg: 'bg-[#E63946]/5',
    icon: AlertCircle,
    iconColor: 'text-[#E63946]',
    badgeColor: 'bg-[#E63946]'
  },
  'at-risk': {
    border: 'border-l-[#FCA311] border-[#FCA311]/30',
    bg: 'bg-[#FCA311]/5',
    icon: AlertTriangle,
    iconColor: 'text-[#FCA311]',
    badgeColor: 'bg-[#FCA311]'
  }
};

export const PipelineNode = ({ 
  tile, 
  showTooltip = false, 
  isDimmed = false,
  showBadge = true,
  draggable = false,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop
}) => {
  const style = isDimmed ? stateStyles.healthy : (stateStyles[tile.state] || stateStyles.healthy);
  const Icon = style.icon;

  const nodeContent = (
    <div
      data-testid={`pipeline-node-${tile.id}-${tile.state}`}
      data-tile-id={tile.id}
      data-lane={tile.lane}
      draggable={draggable && !isDimmed}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={`
        relative min-w-[160px] max-w-[220px] min-h-[72px] p-3 bg-white
        border border-solid border-l-[3px]
        ${isDimmed ? 'border-dashed border-[#B4B2A9] bg-[#f8f9fa] opacity-40 pointer-events-none' : `${style.border} ${style.bg}`}
        rounded-lg transition-all duration-150
        ${(showTooltip && tile.fix) || draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}
        flex-shrink-0 select-none
        hover:shadow-md hover:-translate-y-0.5
      `}
    >
      {/* Pulse animation for broken nodes */}
      {tile.state === 'broken' && !isDimmed && (
        <div className="absolute inset-0 rounded-lg border-2 border-[#E63946] animate-ping opacity-20 pointer-events-none" />
      )}
      
      {/* Drag handle */}
      {draggable && !isDimmed && (
        <div className="absolute top-1.5 left-1.5 text-[#B4B2A9] opacity-0 group-hover:opacity-100 transition-opacity text-xs">
          <GripVertical className="w-3 h-3" />
        </div>
      )}
      
      {/* Status badge */}
      {showBadge && !isDimmed && (
        <div className={`absolute top-2 right-2 w-4 h-4 rounded-full ${style.badgeColor} flex items-center justify-center`}>
          <span className="text-white text-[8px] font-bold">
            {tile.state === 'healthy' ? '✓' : tile.state === 'broken' ? '!' : '⚠'}
          </span>
        </div>
      )}
      
      {/* Content */}
      <div className={`font-mono text-sm font-bold leading-tight ${isDimmed ? 'text-[#6c757d]' : 'text-[#1A3A5C]'}`}>
        {tile.title}
      </div>
      <div className="text-[11px] text-[#5F5E5A] mt-1 leading-snug break-words">
        {tile.sub}
      </div>
    </div>
  );

  if (showTooltip && tile.fix && !isDimmed) {
    return (
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          {nodeContent}
        </TooltipTrigger>
        <TooltipContent 
          data-testid={`tooltip-${tile.id}`}
          side="top"
          className="max-w-md p-4 bg-white border border-[#E2E8F0] shadow-lg rounded-lg"
          sideOffset={8}
        >
          <div className="space-y-3">
            <h4 className="font-semibold text-[#1A3A5C]">{tile.title}</h4>
            <div>
              <div className="text-xs uppercase tracking-widest font-bold text-[#E63946] mb-1">
                What's broken
              </div>
              <p className="text-sm text-[#1A3A5C]/80 leading-relaxed">
                {tile.fix.broken}
              </p>
            </div>
            <div className="border-t border-[#E2E8F0] pt-3">
              <div className="text-xs uppercase tracking-widest font-bold text-[#1D9E75] mb-1">
                How to fix it
              </div>
              <p className="text-sm text-[#1A3A5C]/80 leading-relaxed">
                {tile.fix.ideal}
              </p>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  return nodeContent;
};

export default PipelineNode;
